import React, { ChangeEvent, FC, useCallback, useRef, useState } from 'react'
import { debounce, IconButton, LinearProgress, Popover, Tooltip } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { styled } from '@mui/material/styles'
import InputBase from '@mui/material/InputBase'
import ClearIcon from '@mui/icons-material/Clear'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import { useMutation, useQuery } from '@tanstack/react-query'
import { PopperProps } from '@mui/material/Popper'
import {
  getGeocodeByAddress,
  getSmartyAddress
} from '../../services/create-ride/api'
import SyncIcon from '@mui/icons-material/Sync'

const PopperComponent: FC<PopperProps> = ({ children, className, role }) => (
  <div
    className={className}
    role={role}>
    {typeof children !== 'function' ? children : null}
  </div>
)

const StyledPopper = styled(Popover, {
  shouldForwardProp: (prop: string): boolean => prop !== 'paperWidth'
})(({ paperWidth }: { paperWidth: number | undefined }) => ({
  '.input-wrapper': {
    display: 'flex',
    alignItems: 'center'
  },

  '.MuiInputBase-root': {
    padding: 10,
    width: '100%'
  },

  '.MuiAccordionSummary-content': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },

  '.MuiPopover-paper': {
    width: (paperWidth && paperWidth < 400) ? 400 : paperWidth
  }
}))

type AddressProps = {
  value: any;
  onChange: (value: any) => void;
  name: string;
  renderOutput: React.ReactElement;
  setLogData: (data: any) => void;
}

interface Option {
  address: string;
  entries?: number;
  street_line?: string;
  secondary?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}

const GEOCODE_ADDRESS_LOCATION_TYPE = new Map<string, number>([
  ['ROOFTOP', 1],
  ['RANGE_INTERPOLATED', 2],
  ['GEOMETRIC_CENTER', 4],
  ['APPROXIMATE', 8]
])

const UNKNOWN_LOCATION_TYPE = 8

const AddressAutocomplete = ({ value: initValue, onChange, name, renderOutput, setLogData }: AddressProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  const [pendingValue, setPendingValue] = useState(initValue)
  const [search, setSearch] = useState('')
  const [selectedEntries, setSelectedEntries] = useState('')

  const ariaDescribedby = open ? 'favorite-label' : undefined

  const { mutateAsync: getGeocodeByAddressRequest, isLoading: isLoadingGeocoder } = useMutation(getGeocodeByAddress)

  const { data: smartyAddress, isFetching: isLoadingSmartyAddress } = useQuery({
    staleTime: Infinity,
    keepPreviousData: true,
    queryKey: ['smarty-address', search, selectedEntries],
    queryFn: () => getSmartyAddress(search, selectedEntries)
  })

  const setSearchDebounced: (input: string) => void = useCallback(debounce(input => {
    setSearch(input)
    setSelectedEntries('')
  }, 500), [])

  const handleClick = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    if (pendingValue === '') {
      onChange({
        name,
        lat: null,
        lon: null,
        address: '',
        zip_code: ''
      })
    }
  }

  const handlerOnInput = (event: ChangeEvent<HTMLInputElement>) => {
    setPendingValue(event.target.value)
    setSearchDebounced(event.target.value)
  }

  const handlerOnChangeAutocomplete = async (event: React.SyntheticEvent, value: any, reason: string) => {
    try {
      if (reason === 'selectOption') {
        if (value.entries > 1) {
          const searchUpdate = `${pendingValue} `
          setPendingValue(searchUpdate)
          setSearch(searchUpdate)
          setSelectedEntries(`${value.street_line} ${value.secondary} (${value.entries}) ${value.city} ${value.state} ${value.zipcode}`)
          return
        }
        setOpen(false)
        const { results } = await getGeocodeByAddressRequest(value.address)

        const geocodingItem = results.map(item => ({
          ...item,
          geometry: {
            ...item.geometry,
            location_type: GEOCODE_ADDRESS_LOCATION_TYPE.get(item.geometry.location_type) || UNKNOWN_LOCATION_TYPE
          }
        })).sort((a, b) => a.geometry.location_type - b.geometry.location_type)[0]

        if (!geocodingItem) {
          throw new Error('No results')
        }
        const latLng = geocodingItem.geometry.location

        onChange({
          name,
          lat: latLng.lat(),
          lon: latLng.lng(),
          address: value.address,
          zip_code: value.zipcode
        })

        setLogData({
          pendingValue,
          autocomplete: {
            response: smartyAddress,
            item: value
          },
          geocodeByAddress: {
            geoResponse: results,
            geoItem: geocodingItem
          }
        })
      }
    } catch (e) {
      console.error(e)
      setPendingValue('')
      onChange({
        name,
        lat: null,
        lon: null,
        address: '',
        zip_code: ''
      })
    }
  }

  return (
    <>
      {React.cloneElement(renderOutput, {
        onClick: handleClick,
        focused: open,
        'aria-describedby': ariaDescribedby,
        ref,
        InputProps: {
          ...renderOutput.props.InputProps,
          endAdornment: isLoadingGeocoder ? <SyncIcon /> : renderOutput.props.InputProps.endAdornment
        }
      })}
      <StyledPopper
        id={ariaDescribedby}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        paperWidth={ref.current?.clientWidth}
        anchorEl={ref.current} >
        <Autocomplete
          id={'input-' + name}
          freeSolo
          open
          options={smartyAddress ?? [] as any[]}
          isOptionEqualToValue={option => option.address === initValue}
          getOptionLabel={option => {
            if (typeof option === 'object') {
              return option.address
            } else {
              return option
            }
          }}
          renderOption={RenderOption}
          PopperComponent={PopperComponent as FC<PopperProps>}
          filterOptions={x => x}
          noOptionsText="No found"
          value={pendingValue}
          onChange={handlerOnChangeAutocomplete}
          onInput={handlerOnInput}
          renderInput={params => (
            <>
              {isLoadingSmartyAddress ? <LinearProgress /> : null}
              <div className="input-wrapper">
                <InputBase
                  data-testid="autocomplete-input"
                  inputProps={{
                    ...params.inputProps,
                    value: pendingValue
                  }}
                  ref={params.InputProps.ref}
                  onChange={event => {
                    setPendingValue(event.target.value)
                  }}
                  autoFocus
                  placeholder="Search addrees" />
                <Tooltip title="Clear input">
                  <IconButton onClick={() => {
                    setPendingValue('')
                    setSearch('')
                    setSelectedEntries('')
                  }}>
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset input">
                  <IconButton onClick={() => handlerOnInput({ target: { value: initValue } } as ChangeEvent<HTMLInputElement>)}>
                    <SystemUpdateAltIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          )} />
      </StyledPopper>
    </>
  )
}

const RenderOption = (props: any, option: Option) => (
  <li {...props}>
    {option.entries && option.entries > 1
      ? (
        <span>{option.street_line} <span style={{ color: 'red' }}>{option.secondary ?? ''} ... </span>
          {option.city}, {option.state}, {option.zipcode}
          <span style={{ color: 'red' }}> +{option.entries} show</span>
        </span>
      )
      : option.address}
  </li>
)

export default AddressAutocomplete
